/**
 * EAA-1680: color overrides for H5P's own content-type CSS (multichoice,
 * drag question, drag text, blanks, mark-the-words, single choice, and the
 * shared "joubelui" button) so H5P content follows the host app's theme
 * instead of H5P's hardcoded defaults.
 *
 * H5P content renders directly in the main document (div embed mode, no
 * iframe, no shadow DOM — see H5PPlayerComponent.renderDiv), so it already
 * inherits the design tokens written onto document.documentElement by
 * applyTheme() (packages/shared-styles). This file only needs to remap
 * H5P's own selectors onto those same tokens (hsl(var(--primary)) etc.),
 * the same tokens-only technique used by
 * packages/content-kit/src/styles/skin-playful.css.
 *
 * Ported from a reference override stylesheet
 * (h5p-rest-example-server/h5p/external/css/style.css) that hardcoded a
 * single brand color for both correct/incorrect states. Kept the same
 * selector targets here, but split them into --success/--destructive (this
 * app's existing correct/incorrect semantics everywhere else) instead of
 * merging both into one color, and dropped the reference's non-color rules
 * (hub/tutorial-link hiding, +1/-1 indicator hiding, icon glyph swaps) as
 * out of scope for a color-only pass.
 */
export const H5P_THEME_OVERRIDES_CSS = `
    /* Multichoice */
    .h5p-multichoice .h5p-alternative-container {
        background: color-mix(in srgb, hsl(var(--primary)) 10%, transparent) !important;
    }
    .h5p-multichoice .h5p-correct .h5p-answer-icon:before {
        color: hsl(var(--success)) !important;
    }
    .h5p-multichoice .h5p-wrong .h5p-answer-icon:before {
        color: hsl(var(--destructive)) !important;
    }
    .h5p-multichoice .h5p-answer.h5p-correct .h5p-alternative-container {
        color: hsl(var(--success)) !important;
        border: 2px solid hsl(var(--success)) !important;
        box-shadow: none !important;
    }
    .h5p-multichoice .h5p-answer.h5p-wrong .h5p-alternative-container {
        color: hsl(var(--destructive)) !important;
        border: 2px solid hsl(var(--destructive)) !important;
        box-shadow: none !important;
    }

    /* Drag question — EAA-1857: cap the play area.
       H5P.DragQuestion scales its task to 100% of the container width, so a task
       authored at e.g. 654px was blown up ~3x on a full-width preview and ran off
       the screen. H5P exposes no max, and CSS cannot read the authored px size, so
       the cap is a fixed reading width; the task still scales DOWN on narrow
       viewports because the limit is a max-width, not a width. */
    .h5p-content:has(.h5p-dragquestion) {
        max-width: 56rem;
        margin-inline: auto;
    }

    .h5p-dragquestion .h5p-dropped.h5p-correct,
    .h5p-dragquestion .h5p-dropzone.h5p-correct-answer {
        color: hsl(var(--success)) !important;
        border: 0.1em solid hsl(var(--success)) !important;
        background: color-mix(in srgb, hsl(var(--success)) 15%, transparent) !important;
    }

    /* Drag text */
    .h5p-drag-text [aria-dropeffect].h5p-drag-correct-feedback {
        background: color-mix(in srgb, hsl(var(--success)) 15%, transparent) !important;
    }
    .h5p-drag-text [aria-dropeffect].h5p-drag-wrong-feedback {
        background: color-mix(in srgb, hsl(var(--destructive)) 15%, transparent) !important;
    }
    .h5p-drag-text .h5p-drag-dropped.h5p-drag-draggable-correct {
        color: hsl(var(--success)) !important;
    }
    .h5p-drag-text .h5p-drag-dropped.h5p-drag-draggable-wrong {
        color: hsl(var(--destructive)) !important;
    }

    /* Blanks */
    .h5p-blanks .h5p-correct .h5p-text-input {
        color: hsl(var(--success)) !important;
        background-color: color-mix(in srgb, hsl(var(--success)) 15%, transparent) !important;
        border: 1px solid hsl(var(--success)) !important;
    }
    .h5p-blanks .h5p-wrong .h5p-text-input {
        color: hsl(var(--destructive)) !important;
        background-color: color-mix(in srgb, hsl(var(--destructive)) 15%, transparent) !important;
        border: 1px solid hsl(var(--destructive)) !important;
    }

    /* Mark the words */
    .h5p-mark-the-words [aria-describedby='h5p-description-correct'] {
        color: hsl(var(--success)) !important;
        background-color: color-mix(in srgb, hsl(var(--success)) 15%, transparent) !important;
    }
    .h5p-mark-the-words [aria-describedby='h5p-description-incorrect'] {
        color: hsl(var(--destructive)) !important;
        background-color: color-mix(in srgb, hsl(var(--destructive)) 15%, transparent) !important;
    }

    /* Single choice set */
    li.h5p-sc-alternative .h5p-sc-progressbar {
        background-color: color-mix(in srgb, hsl(var(--primary)) 10%, transparent) !important;
    }

    /* Shared "joubelui" button used across most H5P content types
       (Check, Retry, Show solution, ...) */
    .h5p-joubelui-button {
        background: linear-gradient(
            135deg,
            hsl(var(--primary)) 0%,
            color-mix(in srgb, hsl(var(--primary)) 70%, white) 100%
        ) !important;
    }
    .h5p-joubelui-button:hover {
        background: linear-gradient(
            135deg,
            color-mix(in srgb, hsl(var(--primary)) 85%, black) 0%,
            hsl(var(--primary)) 100%
        ) !important;
    }
`;
